import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./TransactionsBaseComponent.module.css";
import Spinner from "../../../common/Spinner/Spinner";

import { useAuth } from "../../../../context/AuthContext";
import { TransactionsAPI, AccountsAPI } from "../../../../utils/firebase";
import { getUserTransactionsCached } from "../../../../utils/firebase/cache/transactionsCached";
import { normalizeFullPath } from "../UploadDataComponent/utils/tagUtils";

import TransactionsTableComponent from "./TransactionsTableComponent/TransactionsTableComponent";
import DataSourcePill from "../../../common/DataSourcePill/DataSourcePill";

import AddEditTransactionModal from "./AddEditTransactionModal/AddEditTransactionModal";
import MultiAddTransactionsModal from "./MultiAddTransactionsModal/MultiAddTransactionsModal";
import MultiEditTransactionsModal from "./MultiEditTransactionsModal/MultiEditTransactionsModal";
import SelectionToolbar from "./SelectionToolbar/SelectionToolbar";

import TransactionsTableSettingsModal from "./TransactionsTableSettingsModal/TransactionsTableSettingsModal";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faLayerGroup, faGear } from "@fortawesome/free-solid-svg-icons";

import { getSearchParamsFromLocation, filterTransactions } from "../../app-utils/search/filterTransactions";
import QuickDateFilter, { computeRangeISO } from "../../../common/QuickDateFilter/QuickDateFilter";

import { inferAccountDelta } from "../../app-utils/txDelta";

/* 🔹 NEW: summary card */
import TransactionsSummaryCard from "./TransactionsSummaryCard/TransactionsSummaryCard";

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 250];
const FULL_DATA_CAP = 9999;
const todayISO = () => new Date().toISOString().split("T")[0];
const VIEW_OPTS_LS_KEY = "pp:txViewOptions";

const TX_TTL_MS = (() => {
  const v = Number(process.env.REACT_APP_TX_CACHE_TTL_MS);
  return Number.isFinite(v) && v > 0 ? v : 60 * 60 * 1000;
})();

/* ---------- Account selection persistence (by NAME) ---------- */
const SELECTED_ACCOUNT_LS_KEY = "pp:tx.selectedAccountName";

function loadSelectedAccountName() {
  try { return localStorage.getItem(SELECTED_ACCOUNT_LS_KEY) || "all"; }
  catch { return "all"; }
}
function saveSelectedAccountName(name) {
  try { localStorage.setItem(SELECTED_ACCOUNT_LS_KEY, name); } catch { }
}


/* ------------------ TAG NORMALIZATION ------------------ */
function coerceTagArray(val) {
  if (Array.isArray(val)) {
    return Array.from(new Set(val.map(s => normalizeFullPath(String(s || ""))).filter(Boolean)));
  }
  if (typeof val === "string") {
    return Array.from(
      new Set(
        val.split(/[|;,]/g).map(s => normalizeFullPath(String(s || "").trim())).filter(Boolean)
      )
    );
  }
  return [];
}

function normalizeRowTags(row) {
  if (!row || typeof row !== "object") return row;
  const tagPath = coerceTagArray(row.tagPath ?? row.tagPaths);
  const next = { ...row };
  if (tagPath.length) next.tagPath = tagPath;
  delete next.tagPaths;
  return next;
}

/* ---------- helpers ---------- */
function inferColumns(items) {
  const cols = [];
  const seen = new Set();
  for (const row of items || []) {
    if (row && typeof row === "object") {
      for (const k of Object.keys(row)) {
        const key = k === "tagPaths" ? "tagPath" : k;
        if (!seen.has(key)) { seen.add(key); cols.push(key); }
      }
    }
  }
  return cols;
}

function toMillis(v) {
  if (!v) return NaN;
  if (typeof v.toMillis === "function") { try { return v.toMillis(); } catch { return NaN; } }
  if (v instanceof Date) return v.getTime();
  const p = Date.parse(v);
  return Number.isFinite(p) ? p : NaN;
}

/* ✅ Stable sort keys per spec:
   1) date desc
   2) (createdAt || updatedAt || serverTimestamp) desc — first present is used
   3) id desc
*/
function getSortKeys(row) {
  const dateMs = (() => {
    const d = String(row?.date || "");
    const p = /^\d{4}-\d{2}-\d{2}$/.test(d) ? Date.parse(`${d}T00:00:00Z`) : Date.parse(d);
    return Number.isFinite(p) ? p : -Infinity;
  })();


  const createdMs = toMillis(row?.createdAt);
  const updatedMs = toMillis(row?.updatedAt);
  const serverMs = toMillis(row?.serverTimestamp);

  // pick the first present in the requested order: createdAt || updatedAt || serverTimestamp
  const tieMs = Number.isFinite(createdMs)
    ? createdMs
    : (Number.isFinite(updatedMs)
      ? updatedMs
      : (Number.isFinite(serverMs) ? serverMs : -Infinity));

  const idStr = String(row?.id ?? "").toLowerCase();
  return { dateMs, tieMs, idStr };
}

function sortRows(rows) {
  const copy = Array.isArray(rows) ? rows.slice() : [];
  copy.sort((a, b) => {
    const A = getSortKeys(a);
    const B = getSortKeys(b);

    if (A.dateMs !== B.dateMs) return B.dateMs - A.dateMs;
    if (A.tieMs !== B.tieMs) return B.tieMs - A.tieMs;
    return B.idStr.localeCompare(A.idStr);
  });
  return copy;
}

/* ---------- table view options ---------- */
const DEFAULT_VIEW_OPTIONS = {
  density: "compact",
  striped: true,
  stickyFirst: true,
  wrapDescription: false,
  showTagsAsChips: true,
  /* NEW: per-user flag to roll out merged transfers gradually */
  mergedTransfersEnabled: true,
};

/* ------------------ 🔹 NEW: project merged transfers ------------------ */
function projectMergedTransfers(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const groups = new Map(); // transferGroupId -> {inLeg, outLeg}
  const singles = [];

  for (const r of list) {
    const isTransfer = String(r?.typeName || "").toLowerCase() === "transfer";
    const gid = r?.transferGroupId;
    const side = String(r?.transferSide || "").toUpperCase();

    if (isTransfer && gid) {
      const g = groups.get(gid) || { inLeg: null, outLeg: null };
      if (side === "IN") g.inLeg = r;
      else if (side === "OUT") g.outLeg = r;
      groups.set(gid, g);
    } else {
      singles.push(r);
    }
  }

  const merged = [];
  for (const [gid, g] of groups.entries()) {
    const IN = g.inLeg;
    const OUT = g.outLeg;
    if (!IN || !OUT) {
      merged.push(IN || OUT);
      continue;
    }
    const amountAbs = Math.abs(Number(IN.amount ?? OUT.amount ?? 0));
    const date = IN.date || OUT.date || "";
    const description = IN.description || OUT.description || "";
    const tagPath = Array.isArray(IN.tagPath) && IN.tagPath.length ? IN.tagPath : (OUT.tagPath || []);
    const currencyCode = IN.currencyCode || OUT.currencyCode || "";

    merged.push({
      __merged: true,
      __legs: { inId: IN.id, outId: OUT.id },
      id: gid,                         // selection key
      typeName: "transfer",
      amount: amountAbs,
      amountAbs,
      date,
      description,
      tagPath,
      currencyCode,
      fromAccountId: OUT.accountId,
      toAccountId: IN.accountId,
      createdAt: IN.createdAt || OUT.createdAt,
      updatedAt: IN.updatedAt || OUT.updatedAt,
    });
  }
  return sortRows([...singles, ...merged]);
}

export default function TransactionsBaseComponent() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const email = (user?.email || "").trim();

  const [state, setState] = useState({ loading: true, error: null, data: [], missing: false });
  const [allColumns, setAllColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [viewOptions, setViewOptions] = useState(DEFAULT_VIEW_OPTIONS);
  const [fromCache, setFromCache] = useState(null);

  // selection + modals
  const [selectedIds, setSelectedIds] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showMultiAdd, setShowMultiAdd] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  // live accounts
  const [accounts, setAccounts] = useState([]);

  const accountOptions = useMemo(() => {
    const names = Array.from(new Set((accounts || []).map(a => String(a?.name || "").trim()).filter(Boolean)));
    names.sort((a, b) => a.localeCompare(b));
    return names;
  }, [accounts]);

  // 🔹 Select options by NAME (value is the name)
  const accountSelectOptions = useMemo(() => {
    const base = [{ name: "All accounts" }];

    // ensure uniqueness by name
    const names = Array.from(
      new Set((accounts || [])
        .map(a => String(a?.name || "").trim())
        .filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));

    return base.concat(names.map(n => ({ name: n })));
  }, [accounts]);
  /* ---------- 🔹 URL & localStorage-backed account selection (by NAME) ---------- */
  const [selectedAccountName, setSelectedAccountName] = useState(() => {
    const p = new URLSearchParams(location.search);
    const fromUrl = (p.get("account") || "").trim();  // expect name or 'all'
    return fromUrl || loadSelectedAccountName() || "all";
  });

  // keep selection valid if accounts change (e.g., account removed/renamed)
  useEffect(() => {
    if (selectedAccountName === "all") return;
    const exists = (accounts || []).some(a => (a?.name || "").trim() === selectedAccountName);
    if (!exists) setSelectedAccountName("all");
  }, [accounts, selectedAccountName]);

  // FIX 1: This useEffect now ONLY handles URL sync. The LS save is moved to the onChange handler.
  // write selection to URL whenever it changes
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (selectedAccountName === "all") p.delete("account");
    else p.set("account", selectedAccountName);

    // saveSelectedAccountName(selectedAccountName); <-- REMOVED!

    navigate({ pathname: location.pathname, search: p.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountName]);

  // ✅ Only update from URL if the 'account' param EXISTS. (KEEP AS-IS)
  // If it's missing, keep whatever we already have (incl. localStorage-bootstrapped value).
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.has("account")) {
      const urlVal = (p.get("account") || "").trim();
      if (urlVal && urlVal !== selectedAccountName) {
        setSelectedAccountName(urlVal);
      }
      if (!urlVal && selectedAccountName !== "all") {
        setSelectedAccountName("all");
      }
    }
    // if 'account' param is absent -> no-op (preserve current selection)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);



  /* ---------- 🔹 Inline edit commit (aware of merged transfers) ---------- */
  const commitRowPatch = async (row, patch) => {
    if (!user?.uid) throw new Error("Not signed in.");
    if (!row?.id || !patch || typeof patch !== "object") return;

    if (patch.tagPath && !Array.isArray(patch.tagPath)) {
      patch.tagPath = coerceTagArray(patch.tagPath);
    }

    if (row.__merged && row.typeName === "transfer" && row.__legs) {
      setActionBusy(true);
      try {
        await TransactionsAPI.updateTransferGroup({
          user,
          transferGroupId: row.id,
          patch,
        });
        await load({ bypassCache: true });
      } finally {
        setActionBusy(false);
      }
      return;
    }

    await TransactionsAPI.updateManualTransaction({
      user,
      tx: { id: row.id, ...patch },
    });

    setState((s) => {
      const nextData = s.data.map(r => (r.id === row.id ? { ...r, ...patch } : r));
      // If any sorting fields changed, re-sort
      const needsResort = ['date', 'updatedAt', 'createdAt', 'serverTimestamp', 'id'].some(k => k in patch);
      return { ...s, data: needsResort ? sortRows(nextData) : nextData };
    });
  };

  useEffect(() => {
    if (!email) { setAccounts([]); return; }
    let unsub = () => { };
    try {
      unsub = AccountsAPI.subscribeUserAccounts({
        userEmail: email,
        cap: 1000,
        onData: (rows) => setAccounts(rows || []),
        onError: () => setAccounts([]),
      });
    } catch { setAccounts([]); }
    return () => unsub && unsub();
  }, [email]);

  async function load({ bypassCache = false } = {}) {
    if (!email) {
      setState({ loading: false, error: new Error("No user email."), data: [], missing: false });
      setAllColumns([]); setSelectedColumns([]); setFromCache(null);
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { rows, fromCache: cameFromCache } = await getUserTransactionsCached({
        userEmail: email,
        latestFirst: true,
        cap: FULL_DATA_CAP,
        ttlMs: TX_TTL_MS,
        bypassCache,
      });

      const normalized = (rows || []).map(normalizeRowTags);
      const sorted = sortRows(normalized);
      const cols = inferColumns(sorted).filter((k) => k !== "tagPaths");

      await TransactionsAPI.initUserTransactionsConfigIfMissing({ user, allColumns: cols });
      const cfg = await TransactionsAPI.readUserTransactionsConfig({ user });

      const selectedRaw = cfg.exists && Array.isArray(cfg.data?.selectedColumns)
        ? cfg.data.selectedColumns : cols;
      const selectedAliased = selectedRaw.map((k) => (k === "tagPaths" ? "tagPath" : k));
      const selected = selectedAliased.filter((k) => cols.includes(k));
      if (selected.join("|") !== selectedRaw.join("|")) {
        try { await TransactionsAPI.updateUserTransactionsSelectedColumns({ user, selectedColumns: selected }); } catch { }
      }

      let nextView = { ...DEFAULT_VIEW_OPTIONS };
      const remote = cfg?.data?.viewOptions && typeof cfg.data.viewOptions === "object" ? cfg.data.viewOptions : null;
      if (remote) nextView = { ...nextView, ...remote };
      else {
        try {
          const localRaw = localStorage.getItem(VIEW_OPTS_LS_KEY);
          if (localRaw) nextView = { ...nextView, ...(JSON.parse(localRaw) || {}) };
        } catch { }
      }

      setAllColumns(cols);
      setSelectedColumns(selected);
      setViewOptions(nextView);
      setState({ loading: false, error: null, data: sorted, missing: false });
      setFromCache(!!cameFromCache);
    } catch (err) {
      const msg = String(err?.message || "");
      const missing = /user_transactions|COLLECTIONS|assertCollectionName|collection/i.test(msg);
      setState({ loading: false, error: err, data: [], missing });
      setAllColumns([]); setSelectedColumns([]); setFromCache(null);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, email]);

  /* ---------- columns ---------- */
  const handleColumnsChange = async (nextColumns) => {
    const safe = (nextColumns || [])
      .map((k) => (k === "tagPaths" ? "tagPath" : k))
      .filter((k) => allColumns.includes(k));
    setSelectedColumns(safe);
    try { await TransactionsAPI.updateUserTransactionsSelectedColumns({ user, selectedColumns: safe }); } catch { }
  };

  /* ---------- view options ---------- */
  const handleViewOptionsChange = async (partial) => {
    const merged = { ...viewOptions, ...(partial || {}) };
    setViewOptions(merged);
    try {
      if (typeof TransactionsAPI.updateUserTransactionsViewOptions === "function") {
        await TransactionsAPI.updateUserTransactionsViewOptions({ user, viewOptions: merged });
      }
      localStorage.setItem(VIEW_OPTS_LS_KEY, JSON.stringify(merged));
    } catch {
      try { localStorage.setItem(VIEW_OPTS_LS_KEY, JSON.stringify(merged)); } catch { }
    }
  };

  /* ---------- selection ---------- */
  const clearSelection = () => setSelectedIds([]);
  const onToggleRow = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return Array.from(next);
    });
  };
  const onToggleAll = (checked, pageIds) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of pageIds) { if (checked) next.add(id); else next.delete(id); }
      return Array.from(next);
    });
  };
  const getRowId = (row) => row?.id ?? `${row?.accountId}-${row?.date}-${row?.description}`;

  /* ---------- toolbar actions (aware of merged transfer rows) ---------- */
  const openEditIfSingle = () => {
    if (selectedIds.length !== 1) return;
    const id = selectedIds[0];

    const displayedRow = tableById.get(id);
    if (displayedRow) {
      setEditTarget(displayedRow);
      return;
    }

    const rawRow = state.data.find((r) => r.id === id);
    if (rawRow) setEditTarget(rawRow);
  };

  const handleOpenBulkEdit = () => { if (!selectedIds.length) return; setShowBulkEdit(true); };

  const {
    q, account, tag, status, period, isActive: isActiveBase
  } = useMemo(() => getSearchParamsFromLocation(location.search), [location.search]);

  const sp = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const rangeRaw = sp.get("range") || "";
  const dateFromRaw = sp.get("dateFrom") || "";
  const dateToRaw = sp.get("dateTo") || "";
  const isDateActive = !!(rangeRaw || dateFromRaw || dateToRaw);

  const resolved = useMemo(() => {
    let from = dateFromRaw || "";
    let to = dateToRaw || "";
    if (!from && !to && rangeRaw) {
      const r = computeRangeISO(rangeRaw);
      if (r) { from = r.from; to = r.to; }
    }
    return { from, to };
  }, [rangeRaw, dateFromRaw, dateToRaw]);

  const isActive = isActiveBase || isDateActive;

  const filteredRows = useMemo(() => {
    const base = isActiveBase
      ? filterTransactions(state.data, { q, account, tag, status, period })
      : state.data;

    if (!isDateActive) return base;

    const fromMs = resolved.from ? Date.parse(resolved.from) : -Infinity;
    const toMs = resolved.to ? (Date.parse(resolved.to) + 24 * 60 * 60 * 1000 - 1) : +Infinity;

    return base.filter((r) => {
      const ms = Date.parse(String(r?.date || ""));
      return Number.isFinite(ms) && ms >= fromMs && ms <= toMs;
    });
  }, [state.data, isActiveBase, isDateActive, q, account, tag, status, period, resolved.from, resolved.to]);

  /* ---------- 🔹 Balance derivation (preserved for summary) ---------- */
  const resolvedAccount = useMemo(() => {
    if (!account) return null;
    return accounts.find((a) => a.name === account || a.id === account) || null;
  }, [account, accounts]);

  const accountRowsAsc = useMemo(() => {
    if (!resolvedAccount) return [];
    const acctId = resolvedAccount.id;
    const rows = (state.data || [])
      .filter(r => r?.accountId === acctId)
      .map(r => {
        const s = String(r?.date || "").slice(0, 10);
        const iso = /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : "";
        return iso ? { ...r, _iso: iso } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a._iso.localeCompare(b._iso)); // oldest -> newest
    return rows;
  }, [state.data, resolvedAccount?.id]);

  const rowsWithBalance = useMemo(() => {
    if (!resolvedAccount || !state.data) return filteredRows;

    let base = Number(resolvedAccount.accountBalance || 0);
    if (resolved.to) {
      const afterRange = state.data.filter(
        (r) => r.accountId === resolvedAccount.id && r.date > resolved.to
      );
      const sumAfter = afterRange.reduce(
        (acc, tx) => acc + inferAccountDelta(tx, resolvedAccount.id),
        0
      );
      base -= sumAfter;
    }

    let running = base;
    return filteredRows.map((row) => {
      const withBal = { ...row, balance: running };
      running -= inferAccountDelta(row, resolvedAccount.id);
      return withBal;
    });
  }, [filteredRows, resolvedAccount, state.data, resolved.to]);

  /* ---------- 🔹 Projection ONLY for table rendering (feature-flagged) ---------- */
  const tableItems = useMemo(() => {
    const baseRows = isActive ? rowsWithBalance : state.data;
    return viewOptions.mergedTransfersEnabled ? projectMergedTransfers(baseRows) : baseRows;
  }, [isActive, rowsWithBalance, state.data, viewOptions.mergedTransfersEnabled]);

  // Fast lookup map for selection ops over table rows (merged+singles)
  const tableById = useMemo(() => new Map(tableItems.map(r => [r.id, r])), [tableItems]);

  /* ---------- Convert two singles → transfer (wizard-lite) ---------- */
  const selectedRawRows = useMemo(() => {
    // Map selection IDs to raw (non-projected) rows by id
    const m = new Map(state.data.map(r => [r.id, r]));
    return selectedIds.map(id => m.get(id)).filter(Boolean);
  }, [selectedIds, state.data]);

  const canConvertToTransfer = useMemo(() => {
    if (selectedRawRows.length !== 2) return false;
    const [a, b] = selectedRawRows;

    // already transfer? bail
    const isTrans = (x) => String(x?.typeName || "").toLowerCase() === "transfer" || x?.transferGroupId;
    if (isTrans(a) || isTrans(b)) return false;

    // amounts: equal magnitude, opposite signs
    const na = Number(a?.amount); const nb = Number(b?.amount);
    if (!Number.isFinite(na) || !Number.isFinite(nb)) return false;
    if (Math.abs(Math.abs(na) - Math.abs(nb)) > 0.000001) return false;
    if (Math.sign(na) === Math.sign(nb)) return false;

    // accounts: different
    if (!a?.accountId || !b?.accountId || a.accountId === b.accountId) return false;

    // currency + date match for safety
    const sameCcy = (a.currencyCode || "").toUpperCase() === (b.currencyCode || "").toUpperCase();
    const sameDate = String(a.date || "").slice(0, 10) === String(b.date || "").slice(0, 10);
    return sameCcy && sameDate;
  }, [selectedRawRows]);

  const handleConvertToTransfer = async () => {
    if (!canConvertToTransfer) return;
    const [a, b] = selectedRawRows;

    // pick OUT=negative, IN=positive
    const outLeg = Number(a.amount) < 0 ? a : b;
    const inLeg = Number(a.amount) > 0 ? a : b;

    setActionBusy(true);
    try {
      await TransactionsAPI.createTransfer({
        user,
        fromAccountId: outLeg.accountId,
        toAccountId: inLeg.accountId,
        amountAbs: Math.abs(Number(outLeg.amount)),
        currencyCode: outLeg.currencyCode || inLeg.currencyCode,
        date: outLeg.date || inLeg.date,
        description: inLeg.description || outLeg.description || "",
        tagPath: Array.isArray(inLeg.tagPath) && inLeg.tagPath.length ? inLeg.tagPath : (outLeg.tagPath || []),
      });

      // delete originals
      await TransactionsAPI.deleteTransactions({ user, ids: [a.id, b.id] });

      await load({ bypassCache: true });
      setSelectedIds([]);
    } catch (e) {
      alert(String(e?.message || e));
    } finally {
      setActionBusy(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedIds.length) return;
    setActionBusy(true);
    try {
      const ids = [...selectedIds];
      const normalIds = [];
      const transferGroupIds = [];

      for (const id of ids) {
        const row = tableById.get(id);
        if (row && row.__merged && row.typeName === "transfer") transferGroupIds.push(id);
        else normalIds.push(id);
      }

      for (const gid of transferGroupIds) {
        await TransactionsAPI.deleteTransferGroup({ user, transferGroupId: gid });
      }
      if (normalIds.length) {
        await TransactionsAPI.deleteTransactions({ user, ids: normalIds });
      }

      await load({ bypassCache: true });
      setSelectedIds([]);
    } catch (e) { alert(String(e?.message || e)); }
    finally { setActionBusy(false); }
  };

  const handleCopySelected = async () => {
    if (!selectedIds.length) return;
    const today = todayISO();

    setActionBusy(true);
    try {
      for (const id of selectedIds) {
        const row = tableById.get(id);
        if (!row) continue;

        if (row.__merged && row.typeName === "transfer") {
          await TransactionsAPI.createTransfer({
            user,
            fromAccountId: row.fromAccountId,
            toAccountId: row.toAccountId,
            amountAbs: row.amountAbs ?? Math.abs(Number(row.amount || 0)),
            currencyCode: row.currencyCode || "CAD",
            date: today,
            description: row.description || "",
            tagPath: row.tagPath || [],
          });
        } else {
          const tx = {
            ...row,
            id: undefined,
            createdAt: undefined,
            updatedAt: undefined,
            date: today,
            source: "manual",
          };
          await TransactionsAPI.createManualTransaction({ user, tx });
        }
      }
      await load({ bypassCache: true });
      setSelectedIds([]);
    } catch (e) { alert(String(e?.message || e)); }
    finally { setActionBusy(false); }
  };

  // helpers for chips + url updates
  const updateParam = (key, val) => {
    const next = new URLSearchParams(location.search);
    if (!val) next.delete(key); else next.set(key, val);
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: false });

    // 🔹 keep selector + localStorage consistent when clearing 'account'
    if (key === "account" && !val) {
      setSelectedAccountName("all");
      saveSelectedAccountName("all");
    }
  };

  const clearAllSearch = () => {
    navigate({ pathname: location.pathname, search: "" }, { replace: false });
  };

  return (
    <div className="container-fluid">
      <div className="pp-page">
        {/* Header */}
        <div className={`pp-page-header d-flex flex-sm-row flex-column align-items-sm-center align-items-start justify-content-between gap-2 gap-sm-3 ${styles.headerBar}`}>
          <div className={`d-flex align-items-center gap-2 ${styles.titleWrap}`}>
            <h1 className="pp-page-title">
              {isActive ? "Transactions (Search)" : "All Transactions"}
            </h1>
            <select
              className={`form-select form-select-sm ${styles.accountSelect}`}
              value={selectedAccountName}
              // FIX 2: Explicitly save to localStorage in the onChange handler
              onChange={(e) => {
                const newName = e.target.value;
                setSelectedAccountName(newName);
                saveSelectedAccountName(newName); // <-- This is the change
              }}
              aria-label="Select account"
            >
              {/* "All" */}
              <option key="all" value="all">All accounts</option>

              {/* real accounts by name */}
              {accountSelectOptions
                .filter(opt => opt.name !== "All accounts")
                .map(opt => (
                  <option key={opt.name} value={opt.name}>{opt.name}</option>
                ))}
            </select>
            <DataSourcePill
              fromCache={fromCache}
              loading={state.loading}
              onRefresh={() => load({ bypassCache: true })}
            />
          </div>

          {/* Actions */}
          <div className={`pp-page-actions d-flex gap-2 ${styles.actions}`}>
            <button className="btn btn-primary btn-sm btn-compact" onClick={() => setShowAdd(true)}>
              <FontAwesomeIcon icon={faPlus} className={styles.btnIcon} />
              <span className="ms-1">Add</span>
            </button>
            <button className="btn btn-outline-primary btn-sm btn-compact" onClick={() => setShowMultiAdd(true)}>
              <FontAwesomeIcon icon={faLayerGroup} className={styles.btnIcon} />
              <span className="ms-1">Add Multiple</span>
            </button>
            <div>
              <QuickDateFilter
                currentRangeKey={rangeRaw}
                currentFrom={dateFromRaw}
                currentTo={dateToRaw}
                onPick={(sel) => {
                  const next = new URLSearchParams(location.search);
                  if (sel.type === "range") {
                    next.set("range", sel.key);
                    next.delete("dateFrom"); next.delete("dateTo");
                  } else {
                    if (sel.from) next.set("dateFrom", sel.from); else next.delete("dateFrom");
                    if (sel.to) next.set("dateTo", sel.to); else next.delete("dateTo");
                    next.delete("range");
                  }
                  next.delete("period");
                  navigate({ pathname: location.pathname, search: next.toString() });
                }}
                onClear={() => {
                  const next = new URLSearchParams(location.search);
                  next.delete("range"); next.delete("dateFrom"); next.delete("dateTo");
                  navigate({ pathname: location.pathname, search: next.toString() });
                }}
              />
            </div>
            <button
              className="btn btn-outline-secondary btn-sm btn-compact"
              onClick={() => setShowSettings(true)}
              title="Table settings"
              aria-label="Open table settings">
              <FontAwesomeIcon icon={faGear} className={styles.btnIcon} />
            </button>
          </div>
        </div>

        {/* 🔹 Active filter chips + Clear */}
        {isActive && (
          <div className={styles.filterBar}>
            {q && (
              <span className={styles.filterPill}>
                <span className={styles.filterKey}>desc</span>
                <span className={styles.filterValue} title={q}>{q}</span>
                <button
                  className={styles.filterClose}
                  aria-label="Remove description filter"
                  onClick={() => updateParam("q", "")}
                  type="button"
                >
                  ×
                </button>
              </span>
            )}

            {account && (
              <span className={styles.filterPill}>
                <span className={styles.filterKey}>account</span>
                <span className={styles.filterValue} title={account}>{account}</span>
                <button
                  className={styles.filterClose}
                  aria-label="Remove account filter"
                  onClick={() => updateParam("account", "")}
                  type="button"
                >
                  ×
                </button>
              </span>
            )}

            {tag && (
              <span className={styles.filterPill}>
                <span className={styles.filterKey}>tag</span>
                <span className={styles.filterValue} title={tag}>{tag}</span>
                <button
                  className={styles.filterClose}
                  aria-label="Remove tag filter"
                  onClick={() => updateParam("tag", "")}
                  type="button"
                >
                  ×
                </button>
              </span>
            )}

            {status && (
              <span className={styles.filterPill}>
                <span className={styles.filterKey}>status</span>
                <span className={styles.filterValue} title={status}>{status}</span>
                <button
                  className={styles.filterClose}
                  aria-label="Remove status filter"
                  onClick={() => updateParam("status", "")}
                  type="button"
                >
                  ×
                </button>
              </span>
            )}

            {(rangeRaw || dateFromRaw || dateToRaw) && (
              <span className={styles.filterPill}>
                <span className={styles.filterKey}>date</span>
                <span className={styles.filterValue}>
                  {(() => {
                    if (rangeRaw) {
                      const r = computeRangeISO(rangeRaw);
                      return r ? `${r.from} – ${r.to}` : rangeRaw;
                    }
                    return `${dateFromRaw || "…"} – ${dateToRaw || "…"}`;
                  })()}
                </span>
                <button
                  className={styles.filterClose}
                  aria-label="Remove date filter"
                  onClick={() => {
                    const next = new URLSearchParams(location.search);
                    next.delete("range"); next.delete("dateFrom"); next.delete("dateTo");
                    navigate({ pathname: location.pathname, search: next.toString() });
                  }}
                  type="button"
                >
                  ×
                </button>
              </span>
            )}

            {(q || account || tag || status || period || rangeRaw || dateFromRaw || dateToRaw) && (
              <button type="button" className={styles.clearBtn} onClick={clearAllSearch}>
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* 🔹 SUMMARY CARD (kept on raw rows) */}
        {isActive && (
          <TransactionsSummaryCard
            items={isActive ? rowsWithBalance : state.data}
            allItems={state.data}
            accountRows={accountRowsAsc}
            resolvedRange={{ from: resolved.from, to: resolved.to }}
            account={resolvedAccount}
          />
        )}

        <SelectionToolbar
          count={selectedIds.length}
          onClear={clearSelection}
          onDelete={handleDeleteSelected}
          onEdit={openEditIfSingle}
          onBulkEdit={handleOpenBulkEdit}
          onCopy={handleCopySelected}
          busy={actionBusy}
          /* NEW pairing wizard-lite */
          canConvertToTransfer={canConvertToTransfer}
          onConvertToTransfer={handleConvertToTransfer}
        />

        {state.loading && (
          <div className="py-5 d-flex justify-content-center">
            <Spinner label="Loading transactions..." />
          </div>
        )}

        {!state.loading && state.error && (
          <div className="alert alert-danger" role="alert">
            <strong>Failed to load transactions.</strong>
            <div className="small mt-1">{state.error.message}</div>
          </div>
        )}

        {!state.loading && !state.error && (
          <TransactionsTableComponent
            items={tableItems /* 🔹 merged transfers projected here if enabled */}
            columns={selectedColumns}
            density={viewOptions.density}
            stickyFirst={!!viewOptions.stickyFirst}
            striped={!!viewOptions.striped}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            initialRowsPerPage={ROWS_PER_PAGE_OPTIONS[0]}
            selectable
            selectedIds={selectedIds}
            onToggleRow={onToggleRow}
            onToggleAll={onToggleAll}
            getRowId={getRowId}
            inlineEditEnabled
            onCommitPatch={commitRowPatch /* expects (row, patch) */}
            accounts={accounts}
            accountOptions={accountOptions}
          />
        )}

        {/* Settings Modal */}
        <TransactionsTableSettingsModal
          open={showSettings}
          onClose={() => setShowSettings(false)}
          allColumns={allColumns}
          selectedColumns={selectedColumns}
          viewOptions={viewOptions}
          onApply={async ({ columns, options }) => {
            if (Array.isArray(columns)) await handleColumnsChange(columns);
            if (options && Object.keys(options).length) await handleViewOptionsChange(options);
            setShowSettings(false);
          }}
        />

        {/* Modals */}
        <AddEditTransactionModal
          user={user}
          mode="add"
          open={showAdd}
          onClose={() => setShowAdd(false)}
          onSaved={async () => { await load({ bypassCache: true }); setSelectedIds([]); }}
          accountOptions={accountOptions}
          accounts={accounts}
        />
        <AddEditTransactionModal
          user={user}
          mode="edit"
          open={!!editTarget}
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={async () => { await load({ bypassCache: true }); setSelectedIds([]); }}
          accountOptions={accountOptions}
          accounts={accounts}
        />
        <MultiAddTransactionsModal
          user={user}
          open={showMultiAdd}
          onClose={() => setShowMultiAdd(false)}
          onSaved={async () => { await load({ bypassCache: true }); setSelectedIds([]); }}
          accountOptions={accountOptions}
          accounts={accounts}
        />
        <MultiEditTransactionsModal
          user={user}
          open={showBulkEdit}
          rows={state.data.filter(r => selectedIds.includes(r.id))}
          descriptionHints={useMemo(() => {
            const seen = new Set(); const out = [];
            for (const r of state.data) {
              const d = String(r?.description || "").trim();
              if (d && !seen.has(d)) { seen.add(d); out.push(d); }
              if (out.length >= 800) break;
            }
            return out;
          }, [state.data])}
          onClose={() => setShowBulkEdit(false)}
          onSaved={async () => { await load({ bypassCache: true }); setSelectedIds([]); setShowBulkEdit(false); }}
          accountOptions={accountOptions}
          accounts={accounts}
        />
      </div>
    </div>
  );
}